export const agileScrumModule = {
  id: "agile-fundamentals-enterprise",
  title: "Agile & Scrum Fundamentals",
  description: "Master Agile methodologies and Scrum framework for enterprise software development teams.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Apply Agile principles and values in enterprise development environments
- Implement Scrum framework with proper roles, events, and artifacts
- Facilitate effective sprint planning, daily standups, and retrospectives
- Scale Agile practices for large enterprise teams and complex projects

## Prerequisites
- Basic project management experience
- Understanding of software development lifecycle
- Experience working in development teams

## Overview
Agile transformation can increase project success rates by 65% and reduce time to market by 47%. This module teaches proven Agile and Scrum practices used by successful enterprise organizations.

---

## Key Concepts

### Agile Principles and Values

#### The Agile Manifesto
**Individuals and interactions** over processes and tools
**Working software** over comprehensive documentation
**Customer collaboration** over contract negotiation
**Responding to change** over following a plan

#### Core Agile Principles
- Customer satisfaction through early and continuous delivery
- Welcome changing requirements, even late in development
- Deliver working software frequently (weeks rather than months)
- Business people and developers work together daily
- Build projects around motivated individuals
- Face-to-face conversation is the most effective communication
- Working software is the primary measure of progress
- Sustainable development pace for teams
- Continuous attention to technical excellence
- Simplicity—maximizing work not done
- Self-organizing teams produce the best results
- Regular reflection and adjustment for improvement

### Scrum Framework

#### Scrum Roles
**Product Owner**: Manages product backlog, defines user stories, sets priorities
**Scrum Master**: Facilitates process, removes impediments, coaches team
**Development Team**: Cross-functional group that delivers the product

#### Scrum Events
**Sprint**: Fixed time-box (1-4 weeks) for completing work
**Sprint Planning**: Team plans work for upcoming sprint
**Daily Scrum**: 15-minute sync on progress and impediments
**Sprint Review**: Demo completed work to stakeholders
**Sprint Retrospective**: Team reflects on process improvements

#### Scrum Artifacts
**Product Backlog**: Prioritized list of features and requirements
**Sprint Backlog**: Work selected for current sprint
**Increment**: Potentially shippable product increment

---

## Practical Examples

### Example 1: Sprint Planning Session
Structure an effective sprint planning meeting for a 2-week sprint.

**Pre-Planning Preparation**:
- Product Owner refines and prioritizes backlog
- Development team reviews technical dependencies
- Definition of Done is clearly understood

**Sprint Planning Meeting**:
1. Review sprint goal and priority items (30 minutes)
2. Break down user stories into tasks (90 minutes)
3. Estimate effort and commit to sprint scope (60 minutes)
4. Identify risks and dependencies (30 minutes)

**Sprint Planning Outputs**:
- Clear sprint goal statement
- Committed user stories with acceptance criteria
- Task breakdown with effort estimates
- Identified risks and mitigation strategies

### Example 2: Effective Daily Standup
Run productive daily scrum meetings that keep team aligned.

**Daily Scrum Structure**:
Each team member answers three questions:
1. What did I complete yesterday?
2. What will I work on today?
3. What impediments are blocking me?

**Best Practices**:
- Keep to 15-minute timebox
- Focus on work, not status reporting
- Identify impediments for offline resolution
- Update task board in real-time

### Example 3: Sprint Retrospective Facilitation
Conduct retrospectives that drive continuous improvement.

**Retrospective Format**:
1. Set the stage (5 minutes) - Create safe environment
2. Gather data (15 minutes) - What happened this sprint?
3. Generate insights (20 minutes) - Why did it happen?
4. Decide what to do (15 minutes) - What will we change?
5. Close retrospective (5 minutes) - Commit to actions

**Improvement Actions**:
- Limit to 2-3 actionable improvements
- Assign owners and deadlines
- Track progress in next retrospective

---

## Hands-On Lab

### Lab: Agile Project Simulation
Simulate a complete Agile project for an e-commerce platform feature.

**Project Scenario**:
Develop a customer review system with rating functionality, review moderation, and analytics dashboard.

**Sprint 1 (2 weeks)**:
- User story: As a customer, I can submit product reviews
- Tasks: Database schema, API endpoints, basic UI
- Definition of Done: Feature deployed to staging with tests

**Sprint 2 (2 weeks)**:
- User story: As an admin, I can moderate reviews
- Tasks: Admin interface, moderation workflow, notifications
- Definition of Done: Admin can approve/reject reviews

**Sprint 3 (2 weeks)**:
- User story: As a manager, I can view review analytics
- Tasks: Analytics dashboard, reporting features, data visualization
- Definition of Done: Dashboard shows review metrics

**Deliverables**:
- Product backlog with prioritized user stories
- Sprint planning artifacts for each sprint
- Daily scrum simulation records
- Sprint review demonstrations
- Retrospective improvement actions

---

## Assessment

### Agile Implementation Checklist
- [ ] Team understands and follows Agile values and principles
- [ ] Scrum roles are clearly defined and respected
- [ ] All Scrum events are conducted effectively
- [ ] Product backlog is maintained and prioritized
- [ ] Sprint goals are clear and achievable
- [ ] Definition of Done is established and followed
- [ ] Team delivers working software each sprint
- [ ] Retrospectives drive continuous improvement
- [ ] Stakeholders are engaged in the process

### Common Anti-Patterns
**Scrum-but**: Following some Scrum practices while ignoring key principles
**Feature Factory**: Focusing on output instead of outcomes
**Zombie Scrum**: Going through motions without real collaboration
**Waterfall Sprints**: Planning entire project upfront in fixed phases

This module establishes the foundation for successful Agile implementation in enterprise environments.
`,
  duration: 60,
  codeExamples: [
    {
      title: "User Story Template and Acceptance Criteria",
      language: "text",
      code: `// User Story Template
As a [user type]
I want [functionality]
So that [business value]

// Example: E-commerce Review System
As a customer
I want to submit product reviews with ratings
So that I can share my experience and help other customers make informed decisions

// Acceptance Criteria (Given-When-Then format)
Given I am a logged-in customer
When I submit a review with rating and comment
Then the review should be saved and displayed pending moderation

Given I am viewing a product page
When I scroll to the reviews section
Then I should see all approved reviews sorted by date

Given I am submitting a review
When I don't include a rating (1-5 stars)
Then I should see a validation error message

// Definition of Ready Checklist
- [ ] User story follows INVEST criteria (Independent, Negotiable, Valuable, Estimable, Small, Testable)
- [ ] Acceptance criteria are clearly defined
- [ ] Dependencies are identified and resolved
- [ ] Design mockups are available (if needed)
- [ ] Technical approach is understood
- [ ] Story is estimated by the team`
    },
    {
      title: "Sprint Planning Estimation Techniques",
      language: "javascript",
      code: `// Planning Poker Implementation
class PlanningPoker {
  constructor(team) {
    this.team = team;
    this.fibonacciSequence = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89];
    this.estimates = new Map();
  }

  estimateStory(storyId, storyDescription) {
    console.log('Estimating story:', storyDescription);
    
    // Round 1: Initial estimates
    const round1Estimates = this.collectEstimates(storyId, 'initial');
    
    if (this.hasConsensus(round1Estimates)) {
      return this.getConsensusEstimate(round1Estimates);
    }
    
    // Discussion phase
    this.facilitateDiscussion(round1Estimates);
    
    // Round 2: Re-estimate after discussion
    const round2Estimates = this.collectEstimates(storyId, 'revision');
    
    return this.getConsensusEstimate(round2Estimates);
  }

  collectEstimates(storyId, round) {
    const estimates = [];
    
    this.team.forEach(member => {
      const estimate = member.provideEstimate(storyId, this.fibonacciSequence);
      estimates.push({
        member: member.name,
        estimate: estimate,
        reasoning: member.getEstimateReasoning()
      });
    });
    
    this.estimates.set(storyId + '_' + round, estimates);
    return estimates;
  }

  hasConsensus(estimates) {
    const values = estimates.map(e => e.estimate);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Consensus if estimates are within 1 Fibonacci number
    const minIndex = this.fibonacciSequence.indexOf(min);
    const maxIndex = this.fibonacciSequence.indexOf(max);
    
    return (maxIndex - minIndex) <= 1;
  }

  facilitateDiscussion(estimates) {
    const values = estimates.map(e => e.estimate);
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    const lowEstimator = estimates.find(e => e.estimate === min);
    const highEstimator = estimates.find(e => e.estimate === max);
    
    console.log('Discussion needed:');
    console.log('Lowest estimate (' + min + ') by ' + lowEstimator.member + ': ' + lowEstimator.reasoning);
    console.log('Highest estimate (' + max + ') by ' + highEstimator.member + ': ' + highEstimator.reasoning);
  }

  getConsensusEstimate(estimates) {
    const values = estimates.map(e => e.estimate);
    return Math.round(values.reduce((a, b) => a + b) / values.length);
  }
}

// Velocity Tracking
class VelocityTracker {
  constructor() {
    this.sprintHistory = [];
  }

  recordSprintCompletion(sprintNumber, plannedPoints, completedPoints) {
    this.sprintHistory.push({
      sprint: sprintNumber,
      planned: plannedPoints,
      completed: completedPoints,
      date: new Date()
    });
  }

  calculateAverageVelocity(lastNSprints = 3) {
    const recentSprints = this.sprintHistory.slice(-lastNSprints);
    const totalCompleted = recentSprints.reduce((sum, sprint) => sum + sprint.completed, 0);
    return totalCompleted / recentSprints.length;
  }

  getPredictedDeliveryDate(remainingPoints) {
    const avgVelocity = this.calculateAverageVelocity();
    const sprintsNeeded = Math.ceil(remainingPoints / avgVelocity);
    
    const today = new Date();
    const deliveryDate = new Date(today.getTime() + (sprintsNeeded * 14 * 24 * 60 * 60 * 1000)); // 14-day sprints
    
    return {
      sprintsNeeded,
      estimatedDelivery: deliveryDate,
      confidence: this.calculateConfidence()
    };
  }

  calculateConfidence() {
    if (this.sprintHistory.length < 3) return 'Low';
    
    const velocities = this.sprintHistory.slice(-5).map(s => s.completed);
    const avg = velocities.reduce((a, b) => a + b) / velocities.length;
    const variance = velocities.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) / velocities.length;
    const standardDeviation = Math.sqrt(variance);
    
    const coefficientOfVariation = standardDeviation / avg;
    
    if (coefficientOfVariation < 0.2) return 'High';
    if (coefficientOfVariation < 0.4) return 'Medium';
    return 'Low';
  }
}`
    }
  ],
  practice: {
    title: "Facilitate Agile Ceremonies",
    description: "Practice facilitating key Agile ceremonies including sprint planning, daily standups, and retrospectives.",
    starter: `// Basic meeting structure without Agile practices
const projectMeeting = {
  agenda: [
    'Status updates from each team member',
    'Review project timeline',
    'Discuss any issues or blockers',
    'Plan next steps'
  ],
  duration: '60 minutes',
  attendees: ['Project Manager', 'Developers', 'QA', 'Business Analyst']
};`,
    solution: `// Agile ceremony implementation with proper structure

// Sprint Planning Meeting
class SprintPlanning {
  constructor(team, productOwner, sprintDuration = 14) {
    this.team = team;
    this.productOwner = productOwner;
    this.sprintDuration = sprintDuration;
    this.sprintGoal = '';
    this.commitments = [];
  }

  facilitatePlanning() {
    // Part 1: What will we deliver? (Timebox: 50% of meeting)
    this.sprintGoal = this.defineSprintGoal();
    const selectedStories = this.selectBacklogItems();
    
    // Part 2: How will we deliver it? (Timebox: 50% of meeting)
    const taskBreakdown = this.breakdownIntoTasks(selectedStories);
    const capacityCheck = this.validateTeamCapacity(taskBreakdown);
    
    if (capacityCheck.feasible) {
      this.commitments = selectedStories;
      return this.createSprintBacklog();
    } else {
      return this.adjustScope(selectedStories, capacityCheck.suggestedCapacity);
    }
  }

  defineSprintGoal() {
    return this.productOwner.articulateSprintGoal();
  }

  selectBacklogItems() {
    const prioritizedBacklog = this.productOwner.getPrioritizedBacklog();
    const teamVelocity = this.team.getAverageVelocity();
    
    let selectedPoints = 0;
    const selectedItems = [];
    
    for (const item of prioritizedBacklog) {
      if (selectedPoints + item.storyPoints <= teamVelocity) {
        selectedItems.push(item);
        selectedPoints += item.storyPoints;
      } else {
        break;
      }
    }
    
    return selectedItems;
  }
}

// Daily Scrum Implementation
class DailyScrum {
  constructor(team, scrumMaster) {
    this.team = team;
    this.scrumMaster = scrumMaster;
    this.timeboxMinutes = 15;
    this.impediments = [];
  }

  facilitateDaily() {
    const updates = [];
    
    this.team.forEach(member => {
      const update = {
        member: member.name,
        yesterday: member.getYesterdayWork(),
        today: member.getTodayPlan(),
        impediments: member.getImpediments()
      };
      
      updates.push(update);
      
      // Collect impediments for Scrum Master follow-up
      if (update.impediments.length > 0) {
        this.impediments.push(...update.impediments.map(imp => ({
          ...imp,
          reportedBy: member.name,
          reportedDate: new Date()
        })));
      }
    });
    
    // Update sprint board
    this.updateSprintBoard(updates);
    
    // Schedule impediment resolution
    this.scrumMaster.scheduleImpedimentResolution(this.impediments);
    
    return updates;
  }

  updateSprintBoard(updates) {
    updates.forEach(update => {
      update.yesterday.forEach(task => {
        if (task.status === 'completed') {
          this.moveTaskToColumn(task.id, 'done');
        }
      });
      
      update.today.forEach(task => {
        if (task.status === 'in-progress') {
          this.moveTaskToColumn(task.id, 'doing');
        }
      });
    });
  }
}

// Sprint Retrospective Facilitation
class SprintRetrospective {
  constructor(team) {
    this.team = team;
    this.insights = [];
    this.actionItems = [];
  }

  facilitateRetrospective() {
    // 1. Set the stage (5 minutes)
    this.setTheStage();
    
    // 2. Gather data (15 minutes)
    const sprintData = this.gatherData();
    
    // 3. Generate insights (20 minutes)
    this.insights = this.generateInsights(sprintData);
    
    // 4. Decide what to do (15 minutes)
    this.actionItems = this.decideActions(this.insights);
    
    // 5. Close retrospective (5 minutes)
    return this.closeRetrospective();
  }

  gatherData() {
    const data = {
      wentWell: [],
      couldImprove: [],
      puzzling: []
    };
    
    this.team.forEach(member => {
      data.wentWell.push(...member.getWhatWentWell());
      data.couldImprove.push(...member.getWhatCouldImprove());
      data.puzzling.push(...member.getWhatWasPuzzling());
    });
    
    return data;
  }

  generateInsights(data) {
    const insights = [];
    
    // Group similar items
    const groupedImprovements = this.groupSimilarItems(data.couldImprove);
    
    // Prioritize by impact and effort
    groupedImprovements.forEach(group => {
      const insight = {
        theme: group.theme,
        impact: this.assessImpact(group.items),
        effort: this.assessEffort(group.items),
        frequency: group.items.length
      };
      insights.push(insight);
    });
    
    return insights.sort((a, b) => (b.impact / b.effort) - (a.impact / a.effort));
  }

  decideActions(insights) {
    // Limit to top 2-3 actionable improvements
    const topInsights = insights.slice(0, 3);
    
    return topInsights.map(insight => ({
      description: this.formulateAction(insight),
      owner: this.assignOwner(insight),
      dueDate: this.setDueDate(),
      success_criteria: this.defineSuccessCriteria(insight)
    }));
  }
}`
  },
  quiz: {
    question: "What is the recommended timebox for a Daily Scrum meeting?",
    options: [
      "5 minutes",
      "15 minutes",
      "30 minutes", 
      "45 minutes"
    ],
    correctAnswer: 1,
    explanation: "The Daily Scrum is timeboxed to 15 minutes to keep it focused and efficient. This forces the team to stay concise and address detailed discussions offline."
  }
};